import { Campaign } from "../../schema/campaign";
import { CampaignListGroup } from "./types";

/** Lower-cased, null-safe — copy data can carry campaigns without a name. */
const lower = (value: string | undefined | null): string => (value ?? "").toLowerCase();

/**
 * Group a flat list of copyable campaigns into per-source-list groups,
 * sorted alphabetically by list name. Campaign order within a group is
 * preserved from the input.
 */
export const groupCampaignsByList = (campaigns: Campaign[]): CampaignListGroup[] => {
	const groupsById = new Map<number, CampaignListGroup>();

	for (const campaign of campaigns) {
		const existing = groupsById.get(campaign.listId);

		if (existing) {
			existing.campaigns.push(campaign);
		} else {
			groupsById.set(campaign.listId, {
				listId: campaign.listId,
				listName: campaign.listName,
				campaigns: [campaign],
			});
		}
	}

	return Array.from(groupsById.values()).sort((a, b) =>
		lower(a.listName).localeCompare(lower(b.listName))
	);
};

/**
 * Filter groups by a search query. A group whose list name matches is kept in
 * full; otherwise the group is kept only with the campaigns whose name matches.
 * An empty query returns the groups unchanged.
 */
export const filterCampaignGroups = (
	groups: CampaignListGroup[],
	query: string
): CampaignListGroup[] => {
	const normalized = query.trim().toLowerCase();

	if (!normalized) {
		return groups;
	}

	return groups.reduce<CampaignListGroup[]>((acc, group) => {
		if (lower(group.listName).includes(normalized)) {
			acc.push(group);

			return acc;
		}

		const matchedCampaigns = group.campaigns.filter((campaign) =>
			lower(campaign.name).includes(normalized)
		);

		if (matchedCampaigns.length) {
			acc.push({ ...group, campaigns: matchedCampaigns });
		}

		return acc;
	}, []);
};

/** Whether every campaign in the group is currently selected. */
export const isGroupFullySelected = (group: CampaignListGroup, selectedIds: Set<string>): boolean =>
	group.campaigns.length > 0 && group.campaigns.every((campaign) => selectedIds.has(campaign.id));

/** Whether some — but not all — campaigns in the group are selected. */
export const isGroupPartiallySelected = (
	group: CampaignListGroup,
	selectedIds: Set<string>
): boolean => {
	const selectedCount = group.campaigns.filter((campaign) => selectedIds.has(campaign.id)).length;

	return selectedCount > 0 && selectedCount < group.campaigns.length;
};
